import { MetronomeClick, type MetronomeClickKind } from "../../audio/metronome-click";
import { Observable, type Disposable, type ReadonlyObservable } from "../observable";

export type MetronomeListener = (beat: number, isAccent: boolean) => void;

const clamp0to100 = (v: number): number => Math.max(0, Math.min(100, v));

/**
 * High-level metronome component.
 *
 * Wraps the low-level MetronomeClick oscillator and adds beat-scheduling logic.
 * Can be used standalone (continuous metronome) or by the CountInComponent.
 */
export class MetronomeComponent implements Disposable {
  private click: MetronomeClick;
  private listeners = new Set<MetronomeListener>();

  /** UI-scale volume (0–100). The audio gain (0–1) is applied via `applyVolume`. */
  private _volume = new Observable<number>(80, {
    normalize: clamp0to100,
    onChange: (v) => this.applyVolume(v / 100),
  });

  constructor(private readonly ctx: AudioContext) {
    this.click = new MetronomeClick(ctx);
    this.applyVolume(this._volume.value / 100);
  }

  /** Play a single click now. */
  tick(accent = false): void {
    this.click.schedule(this.ctx.currentTime, accent);
  }

  /**
   * Schedule a click at a precise audio time.
   *
   * Accepts either a boolean (legacy: true = accent, false = normal) or an
   * explicit kind (`"accent" | "normal" | "subdivision"`). Subdivision clicks
   * are quieter so the main pulse stays clearly audible above them.
   */
  scheduleAt(time: number, kindOrAccent: MetronomeClickKind | boolean = "normal"): void {
    this.click.schedule(time, kindOrAccent);
  }

  get volume(): ReadonlyObservable<number> {
    return this._volume;
  }

  /** Set volume (UI scale 0–100). */
  setVolume(volume: number): void {
    this._volume.set(volume);
  }

  /** Subscribe to beat events (fired by CountInComponent or future continuous metronome). */
  onBeat(callback: MetronomeListener): () => void {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  /** Emit a beat event to listeners. */
  emitBeat(beat: number, isAccent: boolean): void {
    this.listeners.forEach((cb) => cb(beat, isAccent));
  }

  dispose(): void {
    this.click.dispose();
    this.listeners.clear();
    this._volume.dispose();
  }

  private applyVolume(gain01: number): void {
    this.click.setVolume(gain01);
  }
}
